package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.BillingStatus
import com.dotdrives.model.Budget
import com.dotdrives.model.INVITABLE_ROLES
import com.dotdrives.model.NewBudget
import com.dotdrives.model.NewTag
import com.dotdrives.model.OrgIntegrations
import com.dotdrives.model.OrgInvite
import com.dotdrives.model.OrgMember
import com.dotdrives.model.Subscription
import com.dotdrives.model.Tag
import com.dotdrives.repository.OrgRepository
import com.dotdrives.repository.SettingsRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

/** Shared settings tabs; configuration and provider actions have scoped controllers. */
enum class SettingsTab {
    UsersRoles,
    Process,
    Targets,
    GoalsBudgets,
    Integrations,
    Billing,
    Tags,
}

enum class SettingsGroup { Team, Fundraising, Account }

fun settingsGroup(tab: SettingsTab): SettingsGroup = when (tab) {
    SettingsTab.UsersRoles -> SettingsGroup.Team
    SettingsTab.Process, SettingsTab.Targets, SettingsTab.GoalsBudgets, SettingsTab.Tags -> SettingsGroup.Fundraising
    SettingsTab.Integrations, SettingsTab.Billing -> SettingsGroup.Account
}

data class SettingsUiState(
    val tab: SettingsTab = SettingsTab.UsersRoles,
    val loading: Boolean = false,
    val members: List<OrgMember> = emptyList(),
    val invites: List<OrgInvite> = emptyList(),
    val selectedMemberUserId: String? = null,
    val billingStatus: BillingStatus? = null,
    val error: String? = null,
    val fiscalStartMonth: Int? = null,
    val fiscalStartDay: Int? = null,
    val fiscalLoading: Boolean = false,
    val fiscalSaving: Boolean = false,
    val fiscalFeedback: String? = null,
    val fiscalError: String? = null,

    val budgets: List<Budget> = emptyList(),
    val budgetsLoading: Boolean = false,
    val budgetsLoaded: Boolean = false,
    val budgetsError: String? = null,

    val integrations: OrgIntegrations? = null,
    val integrationsLoading: Boolean = false,
    val integrationsLoaded: Boolean = false,
    val integrationsError: String? = null,

    val subscription: Subscription? = null,
    // True only when the load succeeded and RLS returned nothing (field role) -- a permission
    // state, not a failure. Distinct from subscriptionError, which is a real transient failure.
    val subscriptionDenied: Boolean = false,
    val subscriptionLoading: Boolean = false,
    val subscriptionLoaded: Boolean = false,
    val subscriptionError: String? = null,

    val inviteDialogOpen: Boolean = false,
    val inviteEmail: String = "",
    val inviteRole: String = "member",
    val inviteSending: Boolean = false,
    val inviteError: String? = null,

    val editingBudget: Budget? = null,
    val budgetFeedback: String? = null,
    val tagFeedback: String? = null,
    val editingTag: Tag? = null,
    val addBudgetOpen: Boolean = false,
    val addBudgetSaving: Boolean = false,
    val addBudgetError: String? = null,

    val tags: List<Tag> = emptyList(),
    val tagsLoading: Boolean = false,
    val tagsLoaded: Boolean = false,
    val tagsError: String? = null,
    val addTagOpen: Boolean = false,
    val addTagSaving: Boolean = false,
    val addTagError: String? = null,
)

class SettingsController(
    private val orgRepo: OrgRepository,
    private val settingsRepo: SettingsRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val onFiscalSaved: (String, Int, Int) -> Unit = { _, _, _ -> },
) : DotController<SettingsUiState>(SettingsUiState(), scope) {
    private var mainGeneration = 0L
    private var budgetsGeneration = 0L
    private var integrationsGeneration = 0L
    private var subscriptionGeneration = 0L
    private var tagsGeneration = 0L
    private var fiscalReadGeneration = 0L
    private var fiscalSaveGeneration = 0L

    fun load() {
        loadFiscalPeriod()
        if (!state.value.budgetsLoaded && !state.value.budgetsLoading) loadBudgets()
        val generation = nextMainGeneration()
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                val membersResult = orgRepo.members(orgId)
                if (!isCurrentMain(generation)) return@launch
                membersResult
                    .onSuccess { members ->
                        scope.launch {
                            val invitesResult = orgRepo.invites(orgId)
                            val billingStatusResult = settingsRepo.billingStatus(orgId)
                            if (!isCurrentMain(generation)) return@launch
                            // Neither call may be swallowed into a fabricated "empty"/"no
                            // banner" result on failure. An RLS-empty invites list already
                            // arrives as Result.success(emptyList()) -- a caught exception here
                            // is a genuine transient failure, not a permission result, so on
                            // failure the prior list/banner is kept (not cleared) and the
                            // failure is surfaced as an error instead. billingStatus in
                            // particular backs the lapsed-trial/past-due banner (this module's
                            // non-negotiable #2) -- losing it silently on a flaky RPC call would
                            // hide a real past-due state from every role, field included.
                            val failureMessage = invitesResult.exceptionOrNull()?.message
                                ?: billingStatusResult.exceptionOrNull()?.message
                            val loadError = if (invitesResult.isFailure || billingStatusResult.isFailure) {
                                failureMessage ?: "Something went wrong"
                            } else {
                                null
                            }
                            update { current ->
                                if (isCurrentMain(generation)) {
                                    current.copy(
                                        loading = false,
                                        members = members,
                                        invites = invitesResult.getOrDefault(current.invites),
                                        billingStatus = billingStatusResult.getOrDefault(current.billingStatus),
                                        error = loadError,
                                    )
                                } else {
                                    current
                                }
                            }
                        }
                    }
                    .onFailure { publishMainError(it, generation) }
            } catch (error: Exception) {
                publishMainError(error, generation)
            }
        }
    }

    fun saveFiscalPeriod(month: Int, day: Int) {
        if (month !in 1..12 || day !in 1..28) {
            update { it.copy(fiscalError = "Choose a month and a day from 1 to 28.", fiscalFeedback = null) }
            return
        }
        if (state.value.fiscalStartMonth == null || state.value.fiscalLoading) return
        val generation = ++fiscalSaveGeneration
        update { it.copy(fiscalSaving = true, fiscalError = null, fiscalFeedback = null) }
        scope.launch {
            settingsRepo.setFiscalPeriod(orgId, month, day)
                .onSuccess { if (generation == fiscalSaveGeneration) {
                    fiscalReadGeneration += 1
                    onFiscalSaved(orgId, month, day)
                    update { it.copy(fiscalSaving = false, fiscalLoading = false, fiscalStartMonth = month, fiscalStartDay = day, fiscalFeedback = "Fiscal year start saved.") }
                } }
                .onFailure { error -> if (generation == fiscalSaveGeneration) update { it.copy(fiscalSaving = false, fiscalError = error.message ?: "Could not save fiscal year start.") } }
        }
    }

    fun loadFiscalPeriod() {
        val generation = ++fiscalReadGeneration
        update { it.copy(fiscalLoading = true, fiscalError = null) }
        scope.launch {
            orgRepo.myOrgs().onSuccess { orgs ->
                if (generation != fiscalReadGeneration) return@onSuccess
                val org = orgs.firstOrNull { it.id == orgId }
                update { if (org == null) it.copy(fiscalLoading = false, fiscalStartMonth = null, fiscalStartDay = null, fiscalError = "The selected organization could not be loaded.") else it.copy(fiscalLoading = false, fiscalStartMonth = org.fiscalStartMonth, fiscalStartDay = org.fiscalStartDay) }
            }.onFailure { error -> if (generation == fiscalReadGeneration) update { it.copy(fiscalLoading = false, fiscalStartMonth = null, fiscalStartDay = null, fiscalError = error.message ?: "Could not load fiscal year start.") } }
        }
    }

    fun retry() {
        val current = state.value
        when {
            current.error != null -> load()
            current.tab == SettingsTab.GoalsBudgets -> loadBudgets()
            current.tab == SettingsTab.Integrations -> loadIntegrations()
            current.tab == SettingsTab.Billing -> loadSubscription()
            current.tab == SettingsTab.Tags -> loadTags()
            else -> load()
        }
    }

    fun selectTab(tab: SettingsTab) {
        update { it.copy(tab = tab) }
        when (tab) {
            SettingsTab.Process, SettingsTab.Targets -> Unit
            SettingsTab.GoalsBudgets -> if (!state.value.budgetsLoaded && !state.value.budgetsLoading) loadBudgets()
            SettingsTab.Integrations -> if (!state.value.integrationsLoaded && !state.value.integrationsLoading) loadIntegrations()
            SettingsTab.Billing -> if (!state.value.subscriptionLoaded && !state.value.subscriptionLoading) loadSubscription()
            SettingsTab.Tags -> if (!state.value.tagsLoaded && !state.value.tagsLoading) loadTags()
            SettingsTab.UsersRoles -> if (!state.value.budgetsLoaded && !state.value.budgetsLoading) loadBudgets()
        }
    }

    fun selectMember(userId: String) {
        update { it.copy(selectedMemberUserId = userId) }
    }
    fun closeMember() = update { it.copy(selectedMemberUserId = null) }

    fun openInviteDialog() {
        update { it.copy(inviteDialogOpen = true, inviteEmail = "", inviteRole = "member", inviteError = null) }
    }

    fun closeInviteDialog() {
        update { it.copy(inviteDialogOpen = false, inviteError = null) }
    }

    fun setInviteEmail(value: String) {
        update { it.copy(inviteEmail = value, inviteError = null) }
    }

    fun setInviteRole(value: String) {
        if (value !in INVITABLE_ROLES) return
        update { it.copy(inviteRole = value) }
    }

    fun sendInvite() {
        val current = state.value
        val email = current.inviteEmail.trim()
        if (email.isEmpty()) {
            update { it.copy(inviteError = "Enter an email address") }
            return
        }
        val role = current.inviteRole
        update { it.copy(inviteSending = true, inviteError = null) }
        scope.launch {
            orgRepo.invite(orgId, email, role)
                .onSuccess {
                    val invites = orgRepo.invites(orgId).getOrElse { state.value.invites }
                    update {
                        it.copy(
                            inviteSending = false,
                            inviteDialogOpen = false,
                            invites = invites,
                        )
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update {
                        it.copy(
                            inviteSending = false,
                            inviteError = error.message ?: "Something went wrong",
                        )
                    }
                }
        }
    }

    fun openAddBudget() {
        update { it.copy(addBudgetOpen = true, editingBudget = null, addBudgetError = null, budgetFeedback = null) }
    }

    fun editBudget(budget: Budget) {
        if (budget.orgId != orgId || budget.archived) return
        update { it.copy(addBudgetOpen = true, editingBudget = budget, addBudgetError = null, budgetFeedback = null) }
    }

    fun closeAddBudget() {
        if (state.value.addBudgetSaving) return
        update { it.copy(addBudgetOpen = false, editingBudget = null, addBudgetError = null) }
    }

    fun submitBudget(scope: String, scopeRef: String?, amount: Double, periodStart: String, periodEnd: String) {
        if (state.value.addBudgetSaving) return
        if (!amount.isFinite() || amount <= 0.0 || kotlin.math.abs(amount * 100 - kotlin.math.round(amount * 100)) > 0.00001) {
            update { it.copy(addBudgetError = "Enter an amount greater than zero") }
            return
        }
        if (periodStart.isBlank() || periodEnd < periodStart || periodEnd.isBlank() ||
            scope !in listOf("org", "steward", "phase_template") ||
            (scope == "steward" && state.value.members.none { it.userId == scopeRef && it.deactivatedAt == null })) {
            update { it.copy(addBudgetError = "Choose a valid period and an active steward") }; return
        }
        val editing = state.value.editingBudget
        update { it.copy(addBudgetSaving = true, addBudgetError = null) }
        this.scope.launch {
            val draft = NewBudget(scope, scopeRef, amount, periodStart, periodEnd)
            (if (editing == null) settingsRepo.addBudget(orgId, draft) else settingsRepo.updateBudget(orgId, editing.id, draft))
                .onSuccess { saved ->
                    nextBudgetsGeneration()
                    update {
                        it.copy(
                            budgetsLoading = false,
                            addBudgetSaving = false,
                            addBudgetOpen = false,
                            editingBudget = null,
                            budgets = listOf(saved) + it.budgets.filterNot { row -> row.id == editing?.id },
                            budgetFeedback = "${if (editing == null) "Added" else "Saved"} ${budgetDescription(saved)}.",
                        )
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update {
                        it.copy(
                            addBudgetSaving = false,
                            addBudgetError = error.message ?: "Something went wrong",
                        )
                    }
                }
        }
    }

    fun openAddTag() {
        update { it.copy(addTagOpen = true, editingTag = null, addTagError = null) }
    }

    fun editTag(tag: Tag) {
        if (tag.orgId != orgId || tag.archived) return
        update { it.copy(addTagOpen = true, editingTag = tag, addTagError = null) }
    }

    fun closeAddTag() {
        if (state.value.addTagSaving) return
        update { it.copy(addTagOpen = false, addTagError = null) }
    }

    fun submitTag(name: String, color: String) {
        if (state.value.addTagSaving) return
        val trimmedName = name.trim()
        if (trimmedName.isEmpty() || trimmedName.length > 100 || !Regex("^#[0-9A-Fa-f]{6}$").matches(color)) {
            update { it.copy(addTagError = "Enter a tag name of 1–100 characters and choose a color") }
            return
        }
        val editing = state.value.editingTag
        update { it.copy(addTagSaving = true, addTagError = null) }
        scope.launch {
            (if (editing == null) settingsRepo.addTag(orgId, NewTag(trimmedName, color)) else settingsRepo.updateTag(orgId, editing.id, NewTag(trimmedName, color)))
                .onSuccess { saved ->
                    nextTagsGeneration()
                    update {
                        it.copy(
                            tagsLoading = false,
                            addTagSaving = false,
                            addTagOpen = false,
                            tags = (listOf(saved) + it.tags.filterNot { row -> row.id == saved.id }).sortedBy(Tag::name),
                            tagFeedback = "Saved tag “${saved.name}”.",
                        )
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update {
                        it.copy(
                            addTagSaving = false,
                            addTagError = error.message ?: "Something went wrong",
                        )
                    }
                }
        }
    }

    fun archiveTag(tag: Tag) {
        if (tag.orgId != orgId || state.value.addTagSaving) return
        update { it.copy(addTagSaving = true, tagsError = null) }
        scope.launch {
            settingsRepo.archiveTag(orgId, tag.id).onSuccess {
                nextTagsGeneration()
                update { it.copy(addTagSaving = false, tagsLoading = false, tags = it.tags.filterNot { row -> row.id == tag.id }, tagFeedback = "Archived tag “${tag.name}”. Existing assignments are preserved.") }
            }.onFailure { error ->
                if (error is CancellationException) throw error
                update { it.copy(addTagSaving = false, tagsError = error.message ?: "Could not archive tag") }
            }
        }
    }

    private fun budgetDescription(budget: Budget): String {
        val label = when (budget.scope) {
            "org" -> "Whole organization"
            "steward" -> state.value.members.firstOrNull { it.userId == budget.scopeRef }?.displayName ?: "Former steward"
            else -> "Process template"
        }
        val cents = kotlin.math.round(budget.amount * 100).toLong()
        val whole = (cents / 100).toString().reversed().chunked(3).joinToString(",").reversed()
        val amount = "$whole.${(cents % 100).toString().padStart(2, '0')} USD"
        return "$label budget of $amount for ${budget.periodStart} – ${budget.periodEnd}"
    }

    fun archiveBudget(budget: Budget) {
        if (budget.orgId != orgId || state.value.addBudgetSaving) return
        update { it.copy(addBudgetSaving = true, budgetsError = null) }
        scope.launch {
            settingsRepo.archiveBudget(orgId, budget.id).onSuccess {
                nextBudgetsGeneration()
                update { current -> current.copy(addBudgetSaving = false, budgetsLoading = false, budgets = current.budgets.map { row -> if (row.id == budget.id) row.copy(archived = true) else row }, budgetFeedback = "Archived ${budgetDescription(budget)}. Historical records are preserved.") }
            }.onFailure { error ->
                if (error is CancellationException) throw error
                update { it.copy(addBudgetSaving = false, budgetsError = error.message ?: "Could not remove budget") }
            }
        }
    }

    private fun loadBudgets() {
        val generation = nextBudgetsGeneration()
        update { it.copy(budgetsLoading = true, budgetsError = null) }
        scope.launch {
            settingsRepo.budgets(orgId)
                .onSuccess { budgets ->
                    if (!isCurrentBudgets(generation)) return@onSuccess
                    update {
                        if (isCurrentBudgets(generation)) {
                            it.copy(budgetsLoading = false, budgetsLoaded = true, budgets = budgets, budgetsError = null)
                        } else {
                            it
                        }
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (!isCurrentBudgets(generation)) return@onFailure
                    update {
                        if (isCurrentBudgets(generation)) {
                            it.copy(budgetsLoading = false, budgetsError = error.message ?: "Something went wrong")
                        } else {
                            it
                        }
                    }
                }
        }
    }

    private fun loadTags() {
        val generation = nextTagsGeneration()
        update { it.copy(tagsLoading = true, tagsError = null) }
        scope.launch {
            settingsRepo.tags(orgId)
                .onSuccess { tags ->
                    if (!isCurrentTags(generation)) return@onSuccess
                    update {
                        if (isCurrentTags(generation)) {
                            it.copy(tagsLoading = false, tagsLoaded = true, tags = tags.filterNot { row -> row.archived }, tagsError = null)
                        } else {
                            it
                        }
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (!isCurrentTags(generation)) return@onFailure
                    update {
                        if (isCurrentTags(generation)) {
                            it.copy(tagsLoading = false, tagsError = error.message ?: "Something went wrong")
                        } else {
                            it
                        }
                    }
                }
        }
    }

    private fun loadIntegrations() {
        val generation = nextIntegrationsGeneration()
        update { it.copy(integrationsLoading = true, integrationsError = null) }
        scope.launch {
            settingsRepo.integrations(orgId)
                .onSuccess { integrations ->
                    if (!isCurrentIntegrations(generation)) return@onSuccess
                    update {
                        if (isCurrentIntegrations(generation)) {
                            it.copy(
                                integrationsLoading = false,
                                integrationsLoaded = true,
                                integrations = integrations,
                                integrationsError = null,
                            )
                        } else {
                            it
                        }
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (!isCurrentIntegrations(generation)) return@onFailure
                    update {
                        if (isCurrentIntegrations(generation)) {
                            it.copy(
                                integrationsLoading = false,
                                integrationsError = error.message ?: "Something went wrong",
                            )
                        } else {
                            it
                        }
                    }
                }
        }
    }

    private fun loadSubscription() {
        val generation = nextSubscriptionGeneration()
        update { it.copy(subscriptionLoading = true, subscriptionError = null) }
        scope.launch {
            settingsRepo.subscription(orgId)
                .onSuccess { subscription ->
                    if (!isCurrentSubscription(generation)) return@onSuccess
                    if (subscription != null) {
                        // A readable row: never denied, regardless of anything billing_status
                        // says.
                        update {
                            if (isCurrentSubscription(generation)) {
                                it.copy(
                                    subscriptionLoading = false,
                                    subscriptionLoaded = true,
                                    subscription = subscription,
                                    subscriptionDenied = false,
                                    subscriptionError = null,
                                )
                            } else {
                                it
                            }
                        }
                        return@onSuccess
                    }
                    // Null with no exception is ambiguous by itself: it means either RLS
                    // answered with zero rows (has_perm_strict excludes `field`) or the row
                    // simply doesn't exist yet -- public.create_org never inserts one, only the
                    // Stripe webhook and the seed do, so a brand-new org's *owner* would
                    // otherwise be told they lack permission. billing_status(p_org) is
                    // security-definer and bypasses subscriptions' RLS entirely (it only
                    // re-asserts org membership), so every role can call it: a non-null status
                    // there proves a row exists and was hidden by RLS from this direct read
                    // (denied); no status there means there is truly no row (absent, not an
                    // error) -- see docs/schema.md's controller ruling on this module's
                    // permission shape.
                    //
                    // This fallback call has three distinct outcomes, not two -- a failed RPC
                    // (network error, timeout) must not be folded into "absent" the same way a
                    // failed direct subscription() read must not be folded into "denied". A
                    // caught exception here still means "unknown", not "confidently no row",
                    // so it is surfaced as subscriptionError with a retry banner instead of
                    // being read as proof of absence.
                    val billingStatusResult = settingsRepo.billingStatus(orgId)
                    if (!isCurrentSubscription(generation)) return@onSuccess
                    billingStatusResult
                        .onSuccess { billingStatus ->
                            val denied = billingStatus?.status != null
                            update {
                                if (isCurrentSubscription(generation)) {
                                    it.copy(
                                        subscriptionLoading = false,
                                        subscriptionLoaded = true,
                                        subscription = null,
                                        subscriptionDenied = denied,
                                        subscriptionError = null,
                                        billingStatus = billingStatus ?: it.billingStatus,
                                    )
                                } else {
                                    it
                                }
                            }
                        }
                        .onFailure { error ->
                            if (error is CancellationException) throw error
                            update {
                                if (isCurrentSubscription(generation)) {
                                    it.copy(
                                        subscriptionLoading = false,
                                        subscriptionLoaded = true,
                                        subscription = null,
                                        subscriptionDenied = false,
                                        subscriptionError = error.message ?: "Something went wrong",
                                    )
                                } else {
                                    it
                                }
                            }
                        }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (!isCurrentSubscription(generation)) return@onFailure
                    update {
                        if (isCurrentSubscription(generation)) {
                            it.copy(
                                subscriptionLoading = false,
                                subscriptionDenied = false,
                                subscriptionError = error.message ?: "Something went wrong",
                            )
                        } else {
                            it
                        }
                    }
                }
        }
    }

    private fun publishMainError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrentMain(generation)) return
        update {
            if (isCurrentMain(generation)) {
                it.copy(loading = false, error = error.message ?: "Something went wrong")
            } else {
                it
            }
        }
    }

    private fun nextMainGeneration(): Long {
        mainGeneration += 1
        return mainGeneration
    }

    private fun isCurrentMain(generation: Long): Boolean = generation == mainGeneration

    private fun nextBudgetsGeneration(): Long {
        budgetsGeneration += 1
        return budgetsGeneration
    }

    private fun isCurrentBudgets(generation: Long): Boolean = generation == budgetsGeneration

    private fun nextTagsGeneration(): Long {
        tagsGeneration += 1
        return tagsGeneration
    }

    private fun isCurrentTags(generation: Long): Boolean = generation == tagsGeneration

    private fun nextIntegrationsGeneration(): Long {
        integrationsGeneration += 1
        return integrationsGeneration
    }

    private fun isCurrentIntegrations(generation: Long): Boolean = generation == integrationsGeneration

    private fun nextSubscriptionGeneration(): Long {
        subscriptionGeneration += 1
        return subscriptionGeneration
    }

    private fun isCurrentSubscription(generation: Long): Boolean = generation == subscriptionGeneration
}
